import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ClashTestDetails, ClashSettings } from "@ServerTypes/clashTests/clashTests.schema";

/**
 * The clash test the details panels show. It holds only the clash test last requested: asking for another one
 * empties it, and a response to any request but the latest is dropped, so a reader never sees a previous clash
 * test's data or an out-of-order answer.
 */
export interface ClashDetailsState extends Partial<ClashTestDetails> {
  /** True once the latest request has been answered, with a clash test or without one (no access, deleted). */
  isFetch: boolean;
  /** The clash test the latest request asked for. */
  requestedId?: string;
  /** Sequence number of the latest request. */
  request?: number;
}

const clashDetailsSlice = createSlice({
  name: "clashDetails",
  initialState: { isFetch: false } as ClashDetailsState,
  reducers: {
    requestClashDetails: (state: ClashDetailsState, action: PayloadAction<{ clashTestId: string; request: number }>) =>
      // A refresh of the clash test already shown keeps it on screen until the answer lands.
      state.requestedId === action.payload.clashTestId
        ? { ...state, request: action.payload.request }
        : { isFetch: false, requestedId: action.payload.clashTestId, request: action.payload.request },
    setClashDetails: (
      state: ClashDetailsState,
      action: PayloadAction<{ request: number; result: ClashTestDetails | undefined }>,
    ) =>
      action.payload.request === state.request
        ? { ...action.payload.result, isFetch: true, requestedId: state.requestedId, request: state.request }
        : state,
    /** What a successful `/updateClashTest` changed, on the clash test it changed. */
    patchClashDetails: (
      state: ClashDetailsState,
      action: PayloadAction<{
        id: string;
        changes: Partial<Pick<ClashTestDetails, "name" | "description" | "status">>;
      }>,
    ) => (state._id === action.payload.id ? { ...state, ...action.payload.changes } : state),
    setClashSettings: (state: ClashDetailsState, action: PayloadAction<Partial<ClashSettings>>) => ({
      ...state,
      settings: { ...state.settings, ...action.payload },
    }),
    resetClashDetails: (_state: ClashDetailsState, _action: PayloadAction<void>) => ({ isFetch: false }),
  },
});

const { setClashSettings: _setClashSettingsRaw } = clashDetailsSlice.actions;

export const { requestClashDetails, setClashDetails, patchClashDetails, resetClashDetails } = clashDetailsSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setClashSettings = <T extends Exact<Partial<ClashSettings>, T>>(payload: T) =>
  _setClashSettingsRaw(payload);

export const clashDetails = clashDetailsSlice.reducer;

export default {
  clashDetails,
};
